import { Link } from "react-router-dom";
import { formatDate, formatPhoneNumber } from "../../utils";
import {
  getRouteMileage,
  resolveLegRouteLocation,
} from "../../utils/leg-route-location";
import { getLegOrderNumberLabel } from "../../utils/leg-display";
import { Badge, Button, Popover } from "../../shared";
import { AppType } from "@/types/config";
import { StatusBadge } from "@/components";
import { useDraggable } from "@dnd-kit/core";
import { ClockIcon, PhoneIcon } from "@heroicons/react/24/outline";
import { hasStoredPermission } from "@/utils/authority";
import { PERMISSIONS } from "@/constants/access-control";
import LocationTableCell from "@/pages/Jobs/Legs/LocationTableCell";

const canEditJobs = () => hasStoredPermission(PERMISSIONS.JOBS_EDIT);
import React from "react";
import { format, formatDistanceToNow } from "date-fns";

// Draggable Row Component
const DraggableTableRow: React.FC<{
  children: React.ReactNode;
  job: any;
  className?: string;
}> = ({ children, job, className }) => {
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: `unassigned-job-${job._id}`,
      data: {
        type: "unassigned-job",
        job: job,
      },
    });

  const style = {
    transform: transform
      ? `translate3d(${transform.x}px, ${transform.y}px, 0)`
      : undefined,
    opacity: isDragging ? 0.5 : 1,
    cursor: isDragging ? "grabbing" : "grab",
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className={className}
      {...attributes}
      {...listeners}
    >
      {children}
    </tr>
  );
};

const getUnassignedJobsTableConfig = (
  appType: AppType,
  options: {
    mode?: "unassigned" | "assigned";
    actionLabel?: string;
    showBuildTrip?: boolean;
  } = {},
) => {
  const isService = appType === "serviceRepair";
  const isTrucking = appType === "trucking";
  const mode = options.mode || "unassigned";
  const actionLabel =
    options.actionLabel || (mode === "assigned" ? "Reassign" : "Assign");
  const showBuildTrip = Boolean(options.showBuildTrip);

  const COLUMN_DEFINITIONS = [
    // {
    //   accessorKey: "loadNumber",
    //   // filterFn: (row, id, value) => {
    //   //   return value.includes(row.getValue(id));
    //   // },
    //   accessorFn: (e) => e.loadNumber || e.orderNumber,
    //   id: "loadNumber",
    //   visible: true,
    //   required: true,
    //   minWidth: 250,
    //   editable: isAuthorized("owner,admin"),
    //   cell: ({ getValue, row: { original } }) => {
    //     return (
    //       <Link
    //         className="text-primary-600 hover:underline"
    //         to={`/jobs/${original._id}`}
    //       >
    //         {getValue() || original.orderNumber}
    //       </Link>
    //     );
    //   },
    // },
    {
      header: isTrucking ? "Load Number" : "Job ID",
      accessorKey: isTrucking ? "loadNumber" : "jobId",
      id: isTrucking ? "loadNumber" : "jobId",
      cell: ({ row, table }) => {
        // Safely extract job ID and display value
        const jobId =
          typeof row.original.jobId === "object" && row.original.jobId
            ? row.original.jobId._id || row.original.jobId.id
            : row.original.jobId;

        const displayValue = isTrucking
          ? row.original.jobId?.loadNumber || row.original.loadNumber || "N/A"
          : row.original.jobId?.jobId ||
            row.original.jobId?._id ||
            row.original.jobId ||
            "N/A";

        return (
          <div className="flex items-center gap-2">
            <Link
              to={`/jobs/${jobId}`}
              className="text-primary-600 hover:text-primary-800 hover:underline font-medium"
              onClick={(e) => e.stopPropagation()} // Prevent row drag when clicking link
            >
              {displayValue}
            </Link>
            {/* Show grouping indicator if there are multiple legs for this job */}
            {row.original._legCount > 1 && (
              <Badge color="gray" size="sm" className="text-xs font-medium">
                {row.original._legCount} legs
              </Badge>
            )}
          </div>
        );
      },
      size: 150, // Increased size to accommodate the badge
    },
    ...(isTrucking
      ? [
          {
            header: "Order Number",
            accessorKey: "orderNumber",
            id: "orderNumber",
            cell: ({ row, table }) => {
              const jobId =
                typeof row.original.jobId === "object" && row.original.jobId
                  ? row.original.jobId._id || row.original.jobId.id
                  : row.original.jobId;

              const displayValue = getLegOrderNumberLabel(row.original);
              const isEmptyLeg = displayValue === "Empty leg";

              return (
                <button
                  type="button"
                  className="text-primary-600 hover:text-primary-800 hover:underline font-medium cursor-pointer"
                  onClick={(e) => {
                    e.stopPropagation();
                    const openLegModal = table.options.meta?.openLegModal;
                    if (openLegModal) {
                      openLegModal(row.original);
                    }
                  }}
                >
                  {isEmptyLeg ? "Empty leg — Edit" : displayValue}
                </button>
              );
            },
            size: 120,
          },
        ]
      : []),
    // {
    //   header: "Client",
    //   accessorFn: (row: any) => {
    //     return row.customer?.name;
    //   },
    //   id: "client",
    //   cell: ({ row }) => row.original.customer?.name || "N/A",
    //   size: 150,
    // },
    {
      header: isTrucking ? "Origin" : "Service Location",
      id: "origin",
      cell: ({ row }) => {
        if (isTrucking) {
          const leg = row.original;

          const origin = resolveLegRouteLocation(leg, "origin");

          if (!origin) return "N/A";
          if (typeof origin === "string") return origin;

          // Extract name and address info
          const name = origin.name || "";

          // Format like formatAddress but with name on first line
          if (name || origin.address) {
            if (
              origin.address &&
              typeof origin.address === "object" &&
              origin.address !== null
            ) {
              const {
                address: street,
                city,
                state,
                zipCode,
                zipcode,
              } = origin.address;
              const addressParts = [city, state, zipCode || zipcode].filter(
                Boolean
              );
              const secondLine = addressParts.join(", ");

              return (
                <>
                  {name && <div className="font-medium">{name}</div>}
                  {street && <div>{street}</div>}
                  {secondLine && (
                    <div className="text-sm text-gray-600">{secondLine}</div>
                  )}
                  <div className="flex space-x-1 mt-1">
                    {canEditJobs() && origin.precall && (
                      <Popover
                        className="w-43"
                        position="top"
                        trigger={<Badge>Precall</Badge>}
                        offset={[-12, 0]}
                      >
                        {origin.receivingHours?.open && (
                          <div className="flex items-center">
                            <ClockIcon className="w-4 h-4" />
                            <span className="text-gray-900 font-semibold text-base mx-2">
                              {origin.receivingHours.open}-
                              {origin.receivingHours.close}
                            </span>
                          </div>
                        )}
                        {origin.phone ? (
                          <div className="flex items-center">
                            <PhoneIcon className="w-4 h-4" />
                            <a
                              href={`tel:${origin.phone}`}
                              className="text-primary-600 hover:text-primary-800 hover:underline font-semibold text-base mx-2"
                            >
                              {formatPhoneNumber(origin.phone)}
                            </a>
                          </div>
                        ) : (
                          "Set # for location"
                        )}
                      </Popover>
                    )}
                    {origin.note && (
                      <Popover
                        className="w-43"
                        position="top"
                        trigger={<Badge color="green">Note</Badge>}
                        offset={[-12, 0]}
                      >
                        {origin.note}
                      </Popover>
                    )}
                  </div>
                </>
              );
            } else {
              // Simple fallback for string addresses or when address is a string
              const addressText =
                typeof origin.address === "string" ? origin.address : "";
              return (
                <>
                  {name && <div className="font-medium">{name}</div>}
                  {addressText && <div className="text-sm">{addressText}</div>}
                </>
              );
            }
          } else {
            return "N/A";
          }
        }
        // Service/Repair: use the same exact cell UI as Job Show legs
        const serviceLocation =
          row.original.serviceLocation ||
          row.original.location ||
          row.original.serviceLocation?.address ||
          null;

        if (!serviceLocation) return "N/A";

        // LocationTableCell expects { name, address, phone, note, precall, ... }
        // If older data shape is "serviceLocation.address" (address object), wrap it.
        const normalized =
          serviceLocation?.address && !serviceLocation?.name
            ? {
                ...serviceLocation,
                name: "Service Location",
                address: serviceLocation.address,
              }
            : serviceLocation;

        return <LocationTableCell data={normalized} />;
      },
      size: 200,
    },
    ...(isTrucking
      ? [
          {
            header: "Destination",
            accessorFn: (row) =>
              row.legs?.[0]?.route?.destination?.address?.fullAddress,
            id: "destination",
            cell: ({ row }) => {
              const leg = row.original;
              const destination = resolveLegRouteLocation(leg, "destination");

              if (!destination) return "N/A";
              if (typeof destination === "string") return destination;

              // Extract name and format like origin column
              const name = destination.name || "";

              // Format with name on first line
              if (name || destination.address) {
                if (
                  destination.address &&
                  typeof destination.address === "object" &&
                  destination.address !== null
                ) {
                  const {
                    address: street,
                    city,
                    state,
                    zipCode,
                    zipcode,
                  } = destination.address;
                  const addressParts = [city, state, zipCode || zipcode].filter(
                    Boolean
                  );
                  const secondLine = addressParts.join(", ");

                  return (
                    <>
                      {name && <div className="font-medium">{name}</div>}
                      {street && <div>{street}</div>}
                      {secondLine && (
                        <div className="text-sm text-gray-600">
                          {secondLine}
                        </div>
                      )}
                      <div className="flex space-x-1 mt-1">
                        {canEditJobs() && destination.precall && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge>Precall</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.receivingHours?.open && (
                              <div className="flex items-center">
                                <ClockIcon className="w-4 h-4" />
                                <span className="text-gray-900 font-semibold text-base mx-2">
                                  {destination.receivingHours.open}-
                                  {destination.receivingHours.close}
                                </span>
                              </div>
                            )}
                            {destination.phone ? (
                              <div className="flex items-center">
                                <PhoneIcon className="w-4 h-4" />
                                <a
                                  href={`tel:${destination.phone}`}
                                  className="text-primary-600 hover:text-primary-800 hover:underline font-semibold text-base mx-2"
                                >
                                  {formatPhoneNumber(destination.phone)}
                                </a>
                              </div>
                            ) : (
                              "Set # for location"
                            )}
                          </Popover>
                        )}
                        {destination.note && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge color="green">Note</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.note}
                          </Popover>
                        )}
                      </div>
                    </>
                  );
                } else {
                  // Simple fallback for string addresses
                  const addressText =
                    typeof destination.address === "string"
                      ? destination.address
                      : "";
                  return (
                    <>
                      {name && <div className="font-medium">{name}</div>}
                      {addressText && (
                        <div className="text-sm">{addressText}</div>
                      )}
                      <div className="flex space-x-1 mt-1">
                        {canEditJobs() && destination.precall && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge>Precall</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.receivingHours?.open && (
                              <div className="flex items-center">
                                <ClockIcon className="w-4 h-4" />
                                <span className="text-gray-900 font-semibold text-base mx-2">
                                  {destination.receivingHours.open}-
                                  {destination.receivingHours.close}
                                </span>
                              </div>
                            )}
                            {destination.phone ? (
                              <div className="flex items-center">
                                <PhoneIcon className="w-4 h-4" />
                                <a
                                  href={`tel:${destination.phone}`}
                                  className="text-primary-600 hover:text-primary-800 hover:underline font-semibold text-base mx-2"
                                >
                                  {formatPhoneNumber(destination.phone)}
                                </a>
                              </div>
                            ) : (
                              "Set # for location"
                            )}
                          </Popover>
                        )}
                        {destination.note && (
                          <Popover
                            className="w-43"
                            position="top"
                            trigger={<Badge color="green">Note</Badge>}
                            offset={[-12, 0]}
                          >
                            {destination.note}
                          </Popover>
                        )}
                      </div>
                    </>
                  );
                }
              } else {
                return "N/A";
              }
            },
            size: 200,
          },
        ]
      : []),
    ...(isTrucking && mode === "assigned"
      ? [
          {
            header: "Current Driver",
            id: "driver",
            cell: ({ row }) => row.original?.driver?.fullName || "Unassigned",
            size: 180,
          },
        ]
      : []),
    {
      header: isTrucking ? "Appointment Date" : "Transaction Date",
      accessorKey: isTrucking ? "appointmentDate" : "transactionDate",
      id: "date",
      cell: ({ row }) => {
        const date = isTrucking
          ? row.original.appointmentDate
          : row.original.transactionDate;
        // console.log("date", date);
        return date ? formatDate(date, false, false, true) : "N/A";
      },
      size: 120,
    },
    // {
    //   header: "Status",
    //   accessorKey: "status",
    //   id: "status",
    //   cell: ({ row }) => {
    //     return <StatusBadge status={row.original.status} />;
    //   },
    //   size: 100,
    // },
    // {
    //   header: "Priority",
    //   accessorKey: "priority",
    //   id: "priority",
    //   cell: ({ row }) => (
    //     <Badge
    //       color={
    //         row.original.priority === "high" ||
    //         row.original.priority === "urgent"
    //           ? "destructive"
    //           : row.original.priority === "medium"
    //           ? "warning"
    //           : "secondary"
    //       }
    //     >
    //       {row.original.priority || "low"}
    //     </Badge>
    //   ),
    //   size: 80,
    // },
    ...(isTrucking
      ? [
          {
            header: "ROUTE",
            accessorFn: (row) => row.original?.route?.mileage,
            id: "route",
            cell: ({ row }) => {
              const route = row.original?.route || row.original?.legs?.[0]?.route;
              const mileage = getRouteMileage(route);
              const durationSeconds = route?.fullData?.duration || route?.durationSeconds;

              // Convert seconds to readable duration
              const formatDuration = (seconds) => {
                if (!seconds) return null;

                const totalMinutes = Math.round(seconds / 60);
                const hours = Math.floor(totalMinutes / 60);
                const minutes = totalMinutes % 60;

                if (hours > 0) {
                  return minutes > 0 ? `${hours}h ${minutes}min` : `${hours}h`;
                } else {
                  return `${minutes}min`;
                }
              };

              const duration = formatDuration(durationSeconds);

              // Display both mileage and duration
              const parts = [];
              if (mileage != null) parts.push(`${mileage} mi`);
              if (duration) parts.push(duration);

              return parts.length > 0 ? (
                <div className="text-sm">
                  {parts.map((part, index) => (
                    <div key={index}>{part}</div>
                  ))}
                </div>
              ) : (
                "N/A"
              );
            },
            size: 120,
          },
        ]
      : []),

    {
      id: "actions",
      header: "Actions",
      cell: ({ row, table }) => {
        const buildManifestTrip = table.options.meta?.buildManifestTrip;
        const canBuildTrip =
          showBuildTrip &&
          isTrucking &&
          typeof buildManifestTrip === "function" &&
          !row.original?.manifestV2Id;

        return (
          <div className="flex flex-wrap items-center gap-1">
            <Button
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                table.options.meta?.assignResources(row.original);
              }}
            >
              {actionLabel}
            </Button>
            {isTrucking && typeof table.options.meta?.openLegModal === "function" ? (
              <Button
                type="alternative"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  table.options.meta.openLegModal(row.original);
                }}
              >
                Edit
              </Button>
            ) : null}
            {canBuildTrip ? (
              <Button
                type="alternative"
                size="xs"
                onClick={(e) => {
                  e.stopPropagation();
                  buildManifestTrip(row.original);
                }}
              >
                Build trip
              </Button>
            ) : null}
          </div>
        );
      },
    },
  ];

  const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

  return {
    COLUMN_DEFINITIONS,
    PAGE_SIZE_OPTIONS,
    DraggableTableRow: mode === "unassigned" ? DraggableTableRow : null,
  };
};

export default getUnassignedJobsTableConfig;
